import { Outlet, NavLink, useNavigate, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, FileText, FolderTree, Users, BookOpen, Mail, ImageIcon, Megaphone, MonitorPlay, Settings, LogOut, ExternalLink, UserCog, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ALL = ["super_admin", "editor", "author"];
const STAFF = ["super_admin", "editor"];
const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ALL },
  { to: "/admin/articulos", label: "Artículos", icon: FileText, roles: ALL },
  { to: "/admin/destacados", label: "Destacados", icon: Star, roles: STAFF },
  { to: "/admin/categorias", label: "Categorías", icon: FolderTree, roles: STAFF },
  { to: "/admin/autores", label: "Autores", icon: Users, roles: STAFF },
  { to: "/admin/revistas", label: "Revistas", icon: BookOpen, roles: STAFF },
  { to: "/admin/suscriptores", label: "Suscriptores", icon: Mail, roles: STAFF },
  { to: "/admin/media", label: "Biblioteca", icon: ImageIcon, roles: ALL },
  { to: "/admin/anuncios", label: "Anuncios", icon: Megaphone, roles: STAFF },
  { to: "/admin/hero", label: "Hero", icon: MonitorPlay, roles: STAFF },
  { to: "/admin/configuracion", label: "Configuración", icon: Settings, roles: STAFF },
  { to: "/admin/usuarios", label: "Equipo", icon: UserCog, roles: ["super_admin"] },
];

const ROLE_LABEL = { super_admin: "Super Admin", editor: "Editor", author: "Autor" };

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sunset-muted">Cargando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const nav = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-sunset-bg flex">
      <aside className="w-60 bg-sunset-ink text-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/admin" className="font-serif text-2xl tracking-tight">Sunset RD</Link>
          <p className="kicker text-white/40 text-[9px] mt-1">Admin CMS · {ROLE_LABEL[user.role] || user.role}</p>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${isActive ? "bg-white/10 text-white border-l-2 border-sunset-accent" : "text-white/60 hover:text-white hover:bg-white/5"}`}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-white/10 space-y-3">
          <p className="text-xs text-white/40 truncate">{user.name} · {user.email}</p>
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-white/60 hover:text-white transition-colors">
            <ExternalLink size={14} /> Ver sitio
          </a>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 text-xs text-white/60 hover:text-white transition-colors">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-60 p-8 md:p-10 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
